import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { InputField, SnackbarField } from '@/components/common';
import { createMenu, getMenuById, updateMenu, Menu, queryAllMenuMsg } from '@/services/manageService/menuService';
import { usePageCrud } from '@/hooks/usePageCrud';

interface MenuDetailProps {
  open: boolean;
  menuId?: number | null;
  isAdd?: boolean;
  onClose: (data?: any) => void;
}

interface MenuForm {
  menuId?: number;
  pid: number;
  menuName: string;
  realm: string;
  realmUrl: string;
  realmModule: string;
  menuType: number; // 1: content, 2: menu, 3: button, 4: special
  menuIcon: string;
  remarks: string;
}

const initialForm: MenuForm = {
  pid: 0,
  menuName: '',
  realm: '',
  realmUrl: '',
  realmModule: '',
  menuType: 1,
  menuIcon: '',
  remarks: '',
};

const MenuDetail: React.FC<MenuDetailProps> = ({ open, menuId, isAdd, onClose }) => {
  const { canCreate, canUpdate } = usePageCrud({
    read: 'admin_menu_detail',
    create: 'admin:menu:create',
    update: 'admin:menu:update',
    remove: 'admin:menu:delete',
  });
  const theme = useTheme();
  const firstInputRef = useRef<HTMLInputElement | null>(null);
  const [form, setForm] = useState<MenuForm>({ ...initialForm });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>(
    { open: false, message: '', severity: 'success' }
  );
  const [formError, setFormError] = useState<Record<string, string>>({});
  const [parentMenuOptions, setParentMenuOptions] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    if (open) {
      setTimeout(() => firstInputRef.current?.focus(), 0);
    }
  }, [open]);

  // Load parent menu options for pid select
  useEffect(() => {
    if (!open) return;
    queryAllMenuMsg()
      .then((res: any = {}) => {
        const opts = Object.entries(res).map(([id, name]) => ({ id: Number(id), name: String(name) }));
        setParentMenuOptions(opts);
      })
      .catch(() => setParentMenuOptions([]));
  }, [open]);

  useEffect(() => {
    if (isAdd && open) {
      setForm({ ...initialForm });
      setLoading(false);
    } else if (menuId && open) {
      setLoading(true);
      getUserMenu(menuId)
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuId, open, isAdd]);

  const getUserMenu = async (id: number) => {
    try {
      const res = await getMenuById(id);
      const data = Array.isArray(res) ? (res[0] as any as Menu) : (res as any as Menu);
      const fm: MenuForm = {
        menuId: data.menuId,
        pid: data.pid ?? 0,
        menuName: data.menuName ?? '',
        realm: data.realm ?? '',
        realmUrl: data.realmUrl ?? '',
        realmModule: data.realmModule ?? '',
        menuType: typeof data.menuType === 'number' ? data.menuType : 1,
        menuIcon: data.menuIcon ?? '',
        remarks: data.remarks ?? '',
      };
      // Enforce linkage rule: root must be content
      if ((fm.pid ?? 0) === 0) fm.menuType = 1;
      setForm(fm);
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Load Menu failed!', severity: 'error' });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formError[name]) setFormError((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSelectChange = (name: keyof MenuForm, value: any) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formError[name as string]) setFormError((prev) => ({ ...prev, [name as string]: '' }));
  };

  const handlePidChange = (newPid: number) => {
    setForm((prev) => {
      const next: MenuForm = { ...prev, pid: newPid };
      if (newPid === 0) {
        next.menuType = 1; // root must be content
      } else if (prev.menuType === 1) {
        next.menuType = 2; // default to 'menu' when becoming child
      }
      return next;
    });
    if (formError.pid) setFormError((prev) => ({ ...prev, pid: '' } as any));
    if (formError.menuType) setFormError((prev) => ({ ...prev, menuType: '' }));
  };

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!form.menuName || form.menuName.trim() === '') errors.menuName = 'required';
    // Linkage: pid==0 -> menuType must be 1 (content); else must be 2/3/4
    const mt = Number(form.menuType);
    if (Number(form.pid) === 0) {
      if (mt !== 1) errors.menuType = 'required';
    } else {
      if (![2, 3, 4].includes(mt)) errors.menuType = 'required';
    }
    if (!form.realmUrl || form.realmUrl.trim() === '') errors.realmUrl = 'required';
    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload: Omit<Menu, 'menuId' | 'children' | 'status'> & { remarks: string } = {
        pid: Number(form.pid) || 0,
        menuName: form.menuName,
        realm: form.realm,
        realmUrl: form.realmUrl,
        realmModule: form.realmModule,
        menuType: Number(form.menuType),
        menuIcon: form.menuIcon,
        remarks: form.remarks,
      } as any;
      const res: any = await createMenu(payload as any);
      onClose({ type: 'create', data: (res?.data ?? payload) });
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Menu Create failed!', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async () => {
    if (!validate() || !form.menuId) return;
    setSubmitting(true);
    try {
      const payload: Partial<Menu> & { remarks: string } = {
        ...form,
        menuType: Number(form.menuType),
        pid: Number(form.pid) || 0,
      } as any;
      const res: any = await updateMenu(form.menuId, payload as any);
      onClose({ type: 'update', data: (res?.data ?? payload) });
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Menu Update failed!', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => onClose(undefined);
  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));
  const isRoot = Number(form.pid) === 0;

  if ((isAdd && !open) || (!isAdd && !menuId && !loading)) return null;

  return (
    <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="inherit">{isAdd ? 'Add Menu' : 'Menu Detail'}</Typography>
        <IconButton aria-label="close" onClick={handleClose} sx={{ color: theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {(loading || submitting) ? (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <Box component="form" autoComplete="off" sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <InputField
                  label="Menu Name"
                  name="menuName"
                  value={form.menuName}
                  onChange={handleChange}
                  required
                  fullWidth
                  error={!!formError.menuName}
                  helperText={formError.menuName}
                  inputRef={firstInputRef}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <InputField
                  label="Parent Menu"
                  select
                  fullWidth
                  value={form.pid}
                  onChange={(e) => handlePidChange(Number(e.target.value))}
                >
                  <MenuItem value={0}>None</MenuItem>
                  {parentMenuOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                  ))}
                </InputField>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <InputField
                  label="Menu Type"
                  select
                  fullWidth
                  value={form.menuType}
                  onChange={(e) => handleSelectChange('menuType', Number(e.target.value))}
                  error={!!formError.menuType}
                  helperText={formError.menuType}
                  display={isRoot}
                >
                  {isRoot
                    ? (<MenuItem value={1}>content</MenuItem>)
                    : ([2, 3, 4].map((v) => (
                        <MenuItem key={v} value={v}>
                          {v === 2 ? 'menu' : v === 3 ? 'button' : 'special'}
                        </MenuItem>
                      )))
                  }
                </InputField>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <InputField
                  label="Realm"
                  name="realm"
                  value={form.realm}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <InputField
                  label="Realm URL"
                  name="realmUrl"
                  value={form.realmUrl}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!formError.realmUrl}
                  helperText={formError.realmUrl}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <InputField
                  label="Realm Module"
                  name="realmModule"
                  value={form.realmModule}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <InputField
                  label="Menu Icon"
                  name="menuIcon"
                  value={form.menuIcon}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <InputField
                  label="Remarks"
                  name="remarks"
                  value={form.remarks}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Stack direction="row" spacing={1} sx={{ px: 1, py: 0.5 }}>
          <Button variant="outlined" onClick={handleClose} disabled={submitting}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius }}>Cancel</Button>
          {isAdd ? (
            canCreate && (
              <Button variant="contained" color="secondary" onClick={handleCreate} disabled={submitting}
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius }}>Create</Button>
            )
          ) : (
            canUpdate && (
              <Button variant="contained" color="primary" onClick={handleSave} disabled={submitting}
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius }}>Save</Button>
            )
          )}
        </Stack>
      </DialogActions>
      <SnackbarField
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
      />
    </Dialog>
  );
};

export default MenuDetail;
